import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Check, ArrowRight, LifeBuoy, Mail } from "lucide-react";
import { sendWelcomeEmail } from "@/services/welcomeEmailService";
import { useAuth } from "@/contexts/AuthContext";
import { ReceiptDownload } from "@/components/subscription/ReceiptDownload";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isActivating, setIsActivating] = useState(true);
  const plan = searchParams.get("plan");
  const price = searchParams.get("price");
  const sessionId = searchParams.get("session_id");
  const purchaseDate = new Date().toLocaleDateString();

  useEffect(() => {
    const activateAccountAndSendWelcome = async () => {
      try {
        if (user?.email) {
          const userName = user.name || "there";
          await sendWelcomeEmail(user.email, userName);
          
          toast({
            title: "Welcome to Scienza Health!",
            description: "Check your email for getting started instructions.",
          });
        }
      } catch (error) {
        console.error("Error in activation process:", error);
        toast({
          title: "Activation Notice",
          description: "Your account is active but we couldn't send the welcome email. Our support team has been notified.",
          variant: "destructive",
        });
      } finally {
        setIsActivating(false);
      }
    };

    activateAccountAndSendWelcome();
  }, [user, toast]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 px-4 py-12">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Success Header */}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900">Welcome Aboard!</h1>
          <p className="text-lg text-gray-600">
            Your payment was successful and your account is now active.
          </p>
        </div>

        {/* Activation Status */}
        <Card className="p-6 bg-white shadow-sm border-green-200 border-2">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Account Activation Status</h3>
              <p className="text-gray-600">
                {isActivating ? (
                  "Activating your account..."
                ) : (
                  "Your account is fully activated!"
                )}
              </p>
            </div>
          </div>
        </Card>

        {/* Order Summary */}
        <Card className="p-6 bg-white shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Plan</span>
              <span className="font-medium">{plan}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Amount</span>
              <span className="font-medium">{price}/month</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Purchase Date</span>
              <span className="font-medium">{purchaseDate}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Status</span>
              <span className="text-green-600 font-medium">Active</span>
            </div>
          </div>
          <div className="mt-4">
            <ReceiptDownload
              planName={plan || ""}
              amount={price || ""}
              date={purchaseDate}
              transactionId={sessionId || ""}
            />
          </div>
        </Card>

        {/* Next Steps */}
        <Card className="p-6 bg-white shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Next Steps</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-medium">1</span>
              </div>
              <div>
                <h3 className="font-medium">Complete Your Profile</h3>
                <p className="text-gray-600">Set up your personal information and preferences</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-medium">2</span>
              </div>
              <div>
                <h3 className="font-medium">Take Initial Assessment</h3>
                <p className="text-gray-600">Complete your first cognitive assessment</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 font-medium">3</span>
              </div>
              <div>
                <h3 className="font-medium">Connect with Gia AI</h3>
                <p className="text-gray-600">Set up your AI assistant for personalized care</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Support Information */}
        <Card className="p-6 bg-white shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <LifeBuoy className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium">Need Help?</h3>
              <p className="text-gray-600">Our support team is available 24/7</p>
              <a href="mailto:support@scienzahealth.com" className="text-blue-600 hover:underline">
                support@scienzahealth.com
              </a>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
          <Button
            onClick={() => navigate("/dashboard")}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Go to Dashboard
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}